import { useMemo, useState } from "react";
import { useGetSuggestedAddonsQuery } from "../../shared/api/backendApi";
import {
  useAddItemsToCartMutation,
  useGetCartQuery,
} from "../../shared/api/shopifyCartApi";
import { LoadingSpinner } from "../../shared/pdp/components/LoadingSpinner";
import { PdpNavigationSteps } from "../../shared/pdp/components/PdpNavigationSteps";
import { PortableText } from "../../shared/pdp/components/PortableText";
import { readHalmParametersQueryString } from "../../shared/pdp/utils/halmParameters";
import { openCartDrawer } from "../../shared/pdp/utils/navigationSteps";
import { formatMoney } from "../../shared/pdp/utils/pricing";
import { toShopifyNumericId } from "../../shared/pdp/utils/shopifyIds";
import { AddonCard } from "./AddonCard";
import {
  getDefaultQuantities,
  getProductQuantity,
  getSelectionTotals,
} from "./selection";
import type {
  AddonQuantities,
  AddonSelection,
  PersonalizedAddonsSeason,
  SuggestedAddon,
} from "./types";
import "./styles.css";

const FERTILIZER_SUBSCRIPTION_SKU = "1001";

type PersonalizedAddonsAppProps = {
  season: PersonalizedAddonsSeason;
};

export function PersonalizedAddonsApp({ season }: PersonalizedAddonsAppProps) {
  const halmQueryString = useMemo(readHalmParametersQueryString, []);
  const lawnSize = useMemo(() => {
    const params = new URLSearchParams(halmQueryString);
    const value = Number(
      params.get("adjusted_lawn_size") || params.get("lawn_size"),
    );

    return Number.isFinite(value) && value > 0 ? value : null;
  }, [halmQueryString]);
  const [selection, setSelection] = useState<AddonSelection>({});
  const [areOtherAddonsExpanded, setAreOtherAddonsExpanded] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const cartQuery = useGetCartQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const isCartReady = Boolean(cartQuery.data) || cartQuery.isError;
  const fertilizerSubscriptionSelected =
    cartQuery.data?.items.some(
      (item) =>
        String(item.sku) === FERTILIZER_SUBSCRIPTION_SKU && item.quantity > 0,
    ) ?? false;
  const suggestedAddonsQueryString = useMemo(() => {
    const params = new URLSearchParams(halmQueryString);
    params.set(
      "fertilizer_subscription_selected",
      String(fertilizerSubscriptionSelected),
    );
    params.set("season", season);
    return params.toString();
  }, [fertilizerSubscriptionSelected, halmQueryString, season]);
  const addonsQuery = useGetSuggestedAddonsQuery(
    { queryString: suggestedAddonsQueryString },
    { skip: !isCartReady },
  );
  const [addItemsToCart, addItemsToCartState] = useAddItemsToCartMutation();

  const suggestedAddons = addonsQuery.data?.suggestedAddons ?? [];
  const otherAddons = addonsQuery.data?.otherAddons ?? [];
  const allAddons = useMemo(
    () => [...suggestedAddons, ...otherAddons],
    [otherAddons, suggestedAddons],
  );
  const availableSuggestedAddons = suggestedAddons.filter(
    (addon) => Object.keys(getDefaultQuantities(addon)).length > 0,
  );
  const selectedSuggestedAddons = availableSuggestedAddons.filter(
    (addon) => getProductQuantity(selection[addon.productId]) > 0,
  );
  const missingSuggestedAddons = availableSuggestedAddons.filter(
    (addon) => getProductQuantity(selection[addon.productId]) === 0,
  );
  const selectedAddons = allAddons.filter(
    (addon) => getProductQuantity(selection[addon.productId]) > 0,
  );
  const selectedTotals = getSelectionTotals(allAddons, selection);
  const hasSelectedDiscount =
    selectedTotals.compareAtPrice > selectedTotals.price;
  const missingRecommendationSelection = missingSuggestedAddons.reduce(
    (nextSelection, addon) => {
      nextSelection[addon.productId] = getDefaultQuantities(addon);
      return nextSelection;
    },
    {} as AddonSelection,
  );
  const missingRecommendationTotals = getSelectionTotals(
    missingSuggestedAddons,
    missingRecommendationSelection,
  );
  const isPageLoading = !isCartReady || addonsQuery.isLoading;

  const setAddonQuantities = (
    addon: SuggestedAddon,
    quantities: AddonQuantities,
  ) => {
    setStatusMessage("");
    setSelection((currentSelection) => ({
      ...currentSelection,
      [addon.productId]: quantities,
    }));
  };

  const handleAddAddon = (addon: SuggestedAddon) => {
    setAddonQuantities(addon, getDefaultQuantities(addon));
  };

  const handleQuantityChange = (
    addon: SuggestedAddon,
    variantId: string,
    quantity: number,
  ) => {
    const nextQuantity = Math.max(0, Math.floor(quantity));
    const nextQuantities = {
      ...(selection[addon.productId] ?? {}),
      [variantId]: nextQuantity,
    };

    if (nextQuantity === 0) delete nextQuantities[variantId];
    setAddonQuantities(addon, nextQuantities);
  };

  const handleAddMissingRecommendations = () => {
    setStatusMessage("");
    setSelection((currentSelection) => {
      const nextSelection = { ...currentSelection };

      missingSuggestedAddons.forEach((addon) => {
        nextSelection[addon.productId] = getDefaultQuantities(addon);
      });

      return nextSelection;
    });
  };

  const handleContinue = async () => {
    setStatusMessage("");

    if (!selectedAddons.length) {
      openCartDrawer();
      return;
    }

    try {
      const items = allAddons.flatMap((addon) =>
        addon.variants.flatMap((variant) => {
          const quantity = selection[addon.productId]?.[variant.variantId] ?? 0;
          return quantity > 0
            ? [
                {
                  id: toShopifyNumericId(variant.variantId),
                  quantity,
                },
              ]
            : [];
        }),
      );
      const response = await addItemsToCart({
        items,
        sections: ["cart-drawer", "cart-icon-bubble"],
        sections_url: window.location.pathname,
      }).unwrap();
      openCartDrawer(response.sections);
    } catch {
      setStatusMessage(
        "Die Zusatzprodukte konnten nicht hinzugefügt werden. Bitte versuche es erneut.",
      );
    }
  };

  return (
    <section
      aria-busy={isPageLoading}
      aria-labelledby="personalized-addons-heading"
      className="personalized-addons"
    >
      <div className="personalized-addons__shell">
        {isPageLoading ? (
          <div
            aria-label="Zusatzprodukte werden geladen..."
            className="personalized-addons__loading"
            data-test="personalized-addons-loading"
            role="status"
          >
            <LoadingSpinner />
          </div>
        ) : null}

        {addonsQuery.isError ? (
          <p className="personalized-addons__status" role="alert">
            Die Empfehlungen konnten nicht geladen werden. Bitte lade die Seite
            erneut.
          </p>
        ) : null}

        {!isPageLoading && !addonsQuery.isError && addonsQuery.data ? (
          <>
            <div className="personalized-addons__page-header">
              <PdpNavigationSteps
                navigationSteps={addonsQuery.data.navigationSteps}
              />
              <header className="personalized-addons__intro">
                {addonsQuery.data.heading?.length ? (
                  <div id="personalized-addons-heading">
                    <PortableText
                      className="personalized-addons__heading"
                      emphasisVariant="accent"
                      value={addonsQuery.data.heading}
                    />
                  </div>
                ) : (
                  <h1
                    className="personalized-addons__heading"
                    id="personalized-addons-heading"
                  >
                    Empfohlene Produkte{" "}
                    <span>für deine akuten Rasenprobleme</span>
                  </h1>
                )}
                {fertilizerSubscriptionSelected ? (
                  <p
                    className="personalized-addons__subscription-copy"
                    data-test="personalized-addons-subscription-copy"
                  >
                    Spare <strong>10 %</strong> auf Zusatzprodukte mit deinem
                    Dünger-Abo.
                  </p>
                ) : null}
              </header>
            </div>

            <div className="personalized-addons__content">
              {suggestedAddons.length ? (
                <section
                  aria-labelledby="personalized-addons-suggested-heading"
                  className="personalized-addons__suggested"
                  data-test="personalized-addons-suggested"
                >
                  <div className="personalized-addons__suggested-header">
                    <h2
                      className="personalized-addons__section-heading"
                      id="personalized-addons-suggested-heading"
                    >
                      <span className="personalized-addons__mobile-heading">
                        Einzeln auswählen
                      </span>
                      <span className="personalized-addons__desktop-heading">
                        Empfohlene Zusatzprodukte
                      </span>
                    </h2>
                    {availableSuggestedAddons.length > 1 &&
                    missingSuggestedAddons.length ? (
                      <aside
                        aria-label="Alle Empfehlungen auswählen"
                        className="personalized-addons__bulk-card"
                      >
                        <div className="personalized-addons__bulk-summary">
                          <div
                            aria-hidden="true"
                            className="personalized-addons__bulk-images"
                          >
                            {availableSuggestedAddons
                              .slice(0, 3)
                              .map((addon) => (
                                <span key={addon.productId}>
                                  {addon.imageUrl ? (
                                    <img alt="" src={addon.imageUrl} />
                                  ) : (
                                    <span className="material-symbols-rounded">
                                      grass
                                    </span>
                                  )}
                                </span>
                              ))}
                          </div>
                          <p>
                            {selectedSuggestedAddons.length
                              ? `${selectedSuggestedAddons.length} von ${availableSuggestedAddons.length} Empfehlungen ausgewählt`
                              : `Wir empfehlen: ${availableSuggestedAddons
                                  .map((addon) => addon.title)
                                  .join(" & ")}`}
                          </p>
                        </div>
                        <button
                          className="personalized-addons__bulk-button"
                          data-test="personalized-addons-add-recommendations"
                          onClick={handleAddMissingRecommendations}
                          type="button"
                        >
                          {selectedSuggestedAddons.length
                            ? "Rest hinzufügen"
                            : availableSuggestedAddons.length === 2
                              ? "Beide hinzufügen"
                              : `Alle ${availableSuggestedAddons.length} hinzufügen`}
                          {missingRecommendationTotals.price > 0
                            ? ` · ${formatMoney(missingRecommendationTotals.price)}`
                            : ""}
                        </button>
                      </aside>
                    ) : availableSuggestedAddons.length ? (
                      <p
                        className="personalized-addons__all-selected"
                        data-test="personalized-addons-all-selected"
                      >
                        <span
                          aria-hidden="true"
                          className="material-symbols-rounded"
                        >
                          check_circle
                        </span>
                        <span>Alle Empfehlungen sind ausgewählt</span>
                      </p>
                    ) : null}
                  </div>
                  <div className="personalized-addons__card-list personalized-addons__suggested-list">
                    {suggestedAddons.map((addon) => (
                      <AddonCard
                        addon={addon}
                        key={addon.productId}
                        lawnSize={lawnSize}
                        onAdd={handleAddAddon}
                        onQuantityChange={handleQuantityChange}
                        onRemove={(selectedAddon) =>
                          setAddonQuantities(selectedAddon, {})
                        }
                        quantities={selection[addon.productId]}
                        season={season}
                        showRecommendationReason
                      />
                    ))}
                  </div>
                </section>
              ) : null}

              {otherAddons.length ? (
                <section
                  className={`personalized-addons__other-addons${
                    suggestedAddons.length
                      ? ""
                      : " personalized-addons__other-addons--standalone"
                  }`}
                  data-test="personalized-addons-other"
                >
                  {suggestedAddons.length ? (
                    <>
                      <h2 className="personalized-addons__other-heading">
                        Weitere Zusatzprodukte
                      </h2>
                      <button
                        aria-controls="personalized-addons-other-list"
                        aria-expanded={areOtherAddonsExpanded}
                        className="personalized-addons__other-toggle"
                        data-test="personalized-addons-other-toggle"
                        onClick={() =>
                          setAreOtherAddonsExpanded((isExpanded) => !isExpanded)
                        }
                        type="button"
                      >
                        <span>
                          Weitere Zusatzprodukte ({otherAddons.length})
                        </span>
                        <span
                          aria-hidden="true"
                          className="material-symbols-rounded"
                        >
                          {areOtherAddonsExpanded
                            ? "keyboard_arrow_up"
                            : "keyboard_arrow_down"}
                        </span>
                      </button>
                    </>
                  ) : null}
                  <div
                    className={`personalized-addons__card-list personalized-addons__other-list${
                      !suggestedAddons.length || areOtherAddonsExpanded
                        ? ""
                        : " personalized-addons__other-list--collapsed"
                    }`}
                    id="personalized-addons-other-list"
                  >
                    {otherAddons.map((addon) => (
                      <AddonCard
                        addon={addon}
                        key={addon.productId}
                        lawnSize={lawnSize}
                        onAdd={handleAddAddon}
                        onQuantityChange={handleQuantityChange}
                        onRemove={(selectedAddon) =>
                          setAddonQuantities(selectedAddon, {})
                        }
                        quantities={selection[addon.productId]}
                        season={season}
                        showStartingPrice
                      />
                    ))}
                  </div>
                </section>
              ) : null}

              {!suggestedAddons.length && !otherAddons.length ? (
                <p className="personalized-addons__status">
                  Aktuell sind keine passenden Zusatzprodukte verfügbar.
                </p>
              ) : null}
            </div>

            <footer
              className="personalized-addons__sticky"
              data-test="personalized-addons-sticky"
            >
              <div className="personalized-addons__sticky-content">
                <div className="personalized-addons__sticky-summary">
                  <strong>
                    {selectedAddons.length === 0
                      ? "Keine ausgewählt"
                      : selectedAddons.length === 1
                        ? "1 Zusatzprodukt"
                        : `${selectedAddons.length} Zusatzprodukte`}
                  </strong>
                  <span
                    className={`personalized-addons__sticky-description${
                      selectedAddons.length
                        ? ""
                        : " personalized-addons__sticky-description--empty"
                    }`}
                  >
                    {selectedAddons.length
                      ? selectedAddons.map((addon) => addon.title).join(", ")
                      : "Du kannst später jederzeit nachbestellen"}
                  </span>
                  {selectedAddons.length ? (
                    <span
                      className="personalized-addons__sticky-prices"
                      data-test="personalized-addons-total"
                    >
                      {hasSelectedDiscount ? (
                        <s>{formatMoney(selectedTotals.compareAtPrice)}</s>
                      ) : null}
                      <b
                        className={
                          hasSelectedDiscount
                            ? "personalized-addons__sale-price"
                            : undefined
                        }
                      >
                        {formatMoney(selectedTotals.price)}
                      </b>
                    </span>
                  ) : null}
                </div>
                <button
                  className={`personalized-addons__continue-button${
                    selectedAddons.length
                      ? " personalized-addons__continue-button--primary"
                      : ""
                  }`}
                  data-test="personalized-addons-continue"
                  disabled={addItemsToCartState.isLoading}
                  onClick={() => void handleContinue()}
                  type="button"
                >
                  {addItemsToCartState.isLoading ? <LoadingSpinner /> : null}
                  <span>
                    {selectedAddons.length
                      ? "Zum Warenkorb"
                      : "Ohne Zusatzprodukte weiter"}
                  </span>
                </button>
              </div>
              {statusMessage ? (
                <p
                  className="personalized-addons__submit-error"
                  data-test="personalized-addons-error"
                  role="alert"
                >
                  {statusMessage}
                </p>
              ) : null}
            </footer>
          </>
        ) : null}
      </div>
    </section>
  );
}
